/**
 * 基础数据公开 API 服务层
 * @description 对接后端三类基础数据公开 API（文件格式 / 任务类型 / 软件列表）
 * @since Sprint_L1b_S1_FE 任务 1.1
 * @see Sprint_L1b_S1_FE_Frontend_Constants.md
 */

import type { ApiResponse } from '@/types/api'
import { wrapGet } from '@/utils/request'

// ============================================================
// 类型定义 — 与后端 Public Schema 精确对齐
// 后端返回 snake_case JSON，前端类型定义与之一致
// ============================================================

/**
 * 文件格式（对齐后端 FileFormatPublic — models.py:1176）
 * @example { code: "step", name: "STEP/STP", extension: ".step,.stp", category: "3D" }
 */
export interface FileFormat {
  id: number
  code: string
  name: string
  extension: string
  category: string
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * 任务类型（对齐后端 TaskTypePublic — models.py:1224）
 * @example { code: "mechanical_design", name: "机械设计", icon: "mechanical" }
 */
export interface TaskTypeItem {
  id: number
  code: string
  name: string
  icon: string | null
  keywords: string[]
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

/**
 * 软件列表项（对齐后端 ModelSoftwarePublic — models.py:1090）
 * 注意：ModelSoftwarePublic 不含 created_at/updated_at，仅有 id/name/icon/is_active
 */
export interface SoftwareItem {
  id: number
  name: string
  icon: string | null
  is_active: boolean
}

/** 文件格式查询参数 */
export interface FileFormatParams {
  /** 按类别筛选（可选），如 "3D" / "2D" / "doc" / "image" */
  category?: string
}

// ============================================================
// API 服务对象
// ============================================================

export const baseDataApi = {
  /**
   * 获取文件格式列表
   * 后端路由: GET /api/file-formats
   * 可选参数: ?category=3D
   */
  async getFileFormats(params?: FileFormatParams): Promise<ApiResponse<FileFormat[]>> {
    return wrapGet<FileFormat[]>('/file-formats', params ? { params } : undefined)
  },

  /**
   * 获取任务类型列表
   * 后端路由: GET /api/task-types
   */
  async getTaskTypes(): Promise<ApiResponse<TaskTypeItem[]>> {
    return wrapGet<TaskTypeItem[]>('/task-types')
  },

  /**
   * 获取已激活软件列表
   * 后端路由: GET /api/software
   */
  async getSoftwareList(): Promise<ApiResponse<SoftwareItem[]>> {
    return wrapGet<SoftwareItem[]>('/software')
  },
}

export default baseDataApi
